import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft, RefreshCw, Send, Loader2, Clock, CheckCircle,
  XCircle, AlertCircle, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REFUND_REASONS = [
  { value: 'insatisfacao_servico', label: 'Insatisfação com o serviço' },
  { value: 'cobranca_indevida', label: 'Cobrança indevida' },
  { value: 'cobranca_duplicada', label: 'Cobrança duplicada' },
  { value: 'nao_utilizei', label: 'Não utilizei o serviço' },
  { value: 'problema_tecnico', label: 'Problema técnico impediu o uso' },
  { value: 'cancelamento_assinatura', label: 'Cancelamento de assinatura' },
  { value: 'creditos_nao_recebidos', label: 'Créditos não foram creditados' },
  { value: 'consultoria_insatisfatoria', label: 'Consultoria insatisfatória' },
  { value: 'outro', label: 'Outro motivo' }
];

const RefundRequestPage = ({ onBack }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [formData, setFormData] = useState({
    reason: '',
    details: '',
    transaction_id: ''
  });

  useEffect(() => {
    if (token) loadMyRequests();
  }, [token]);

  const loadMyRequests = async () => {
    try {
      const res = await axios.get(`${API}/refund-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRequests(res.data);
    } catch (err) {
      console.error('Error loading refund requests:', err);
    } finally {
      setLoadingRequests(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.reason) {
      toast.error('Selecione o motivo do reembolso');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/refund-request`, {
        reason: formData.reason,
        details: formData.details,
        transaction_id: formData.transaction_id
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Solicitação de reembolso enviada com sucesso!');
      setFormData({ reason: '', details: '', transaction_id: '' });
      await loadMyRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar solicitação');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', label: 'Em análise', icon: <Clock className="w-3 h-3" /> },
      approved: { bg: 'rgba(34,197,94,0.15)', color: '#22c55e', label: 'Aprovado', icon: <CheckCircle className="w-3 h-3" /> },
      denied: { bg: 'rgba(239,68,68,0.15)', color: '#ef4444', label: 'Negado', icon: <XCircle className="w-3 h-3" /> }
    };
    const s = styles[status] || styles.pending;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', background: s.bg, color: s.color, padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: '600' }}>
        {s.icon} {s.label}
      </span>
    );
  };

  const getReasonLabel = (value) => {
    const found = REFUND_REASONS.find(r => r.value === value);
    return found ? found.label : value;
  };

  return (
    <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }} data-testid="refund-request-page">
      <Button variant="ghost" onClick={onBack} style={{ marginBottom: '1rem' }} data-testid="refund-back-btn">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Voltar
      </Button>

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
        <RefreshCw className="w-7 h-7" style={{ color: '#00ffff' }} />
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Solicitação de Reembolso</h1>
      </div>
      <p style={{ color: '#aaa', fontSize: '0.85rem', marginBottom: '2rem' }}>
        Solicite o reembolso de uma compra ou assinatura. Analisaremos sua solicitação em até 5 dias úteis.
      </p>

      {/* Refund Request Form */}
      <Card style={{ background: '#1a1a1a', border: '1px solid #333', marginBottom: '2rem' }}>
        <CardHeader>
          <CardTitle style={{ fontSize: '1.1rem' }}>Nova Solicitação</CardTitle>
          <CardDescription style={{ color: '#aaa' }}>Preencha os dados abaixo para solicitar um reembolso</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={{ color: '#ccc', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Motivo do reembolso *</label>
              <select
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                data-testid="refund-reason-select"
                style={{
                  width: '100%',
                  padding: '0.6rem 0.75rem',
                  background: '#0f0f0f',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  color: '#eee',
                  fontSize: '0.9rem',
                  appearance: 'auto'
                }}
              >
                <option value="" style={{ color: '#888' }}>Selecione o motivo...</option>
                {REFUND_REASONS.map(r => (
                  <option key={r.value} value={r.value} style={{ color: '#eee', background: '#1a1a1a' }}>{r.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>ID da transação (opcional)</label>
              <Input
                value={formData.transaction_id}
                onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                placeholder="Ex: pi_1234..."
                style={{ background: '#0f0f0f', border: '1px solid #444', color: '#eee' }}
                data-testid="refund-transaction-input"
              />
            </div>

            <div>
              <label style={{ color: '#ccc', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>Detalhes adicionais</label>
              <textarea
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                placeholder="Descreva o motivo do reembolso em mais detalhes..."
                rows={4}
                data-testid="refund-details-input"
                style={{
                  width: '100%',
                  background: '#0f0f0f',
                  border: '1px solid #444',
                  borderRadius: '6px',
                  padding: '0.75rem',
                  color: '#eee',
                  fontSize: '0.9rem',
                  resize: 'vertical',
                  fontFamily: 'inherit'
                }}
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !formData.reason}
              data-testid="refund-submit-btn"
              style={{ background: '#00ffff', color: '#000', fontWeight: '600', alignSelf: 'flex-start' }}
            >
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Enviar Solicitação
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* My Refund Requests History */}
      <Card style={{ background: '#1a1a1a', border: '1px solid #333' }}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <CardTitle style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FileText className="w-5 h-5" style={{ color: '#00ffff' }} />
              Minhas Solicitações
            </CardTitle>
            <Badge variant="secondary">{myRequests.length}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {loadingRequests ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#00ffff' }} />
            </div>
          ) : myRequests.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa' }}>
              <RefreshCw style={{ width: '40px', height: '40px', margin: '0 auto 0.75rem', opacity: 0.3 }} />
              <p>Nenhuma solicitação de reembolso</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {myRequests.map((req) => (
                <div
                  key={req.id}
                  style={{
                    background: '#0f0f0f',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    padding: '1rem'
                  }}
                  data-testid={`refund-request-${req.id}`}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                    <div>
                      <p style={{ fontWeight: '600', fontSize: '0.9rem', color: '#eee' }}>{getReasonLabel(req.reason)}</p>
                      {req.details && <p style={{ color: '#aaa', fontSize: '0.8rem', marginTop: '0.25rem' }}>{req.details}</p>}
                    </div>
                    {getStatusBadge(req.status)}
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: '#888' }}>
                    <span>Enviado em {new Date(req.created_at).toLocaleDateString('pt-BR')}</span>
                    {req.resolved_at && <span>Resolvido em {new Date(req.resolved_at).toLocaleDateString('pt-BR')}</span>}
                  </div>
                  {req.admin_notes && (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#1a1a1a', borderRadius: '4px', border: '1px solid #333' }}>
                      <p style={{ fontSize: '0.8rem', color: '#aaa' }}>
                        <strong style={{ color: '#ccc' }}>Resposta:</strong> {req.admin_notes}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RefundRequestPage;
